/**
 * TodayPanel — dealer-floor "today's enrollments" widget.
 *
 * Three counts + three short lists, refreshed every 30s:
 *   - Enrolled today          (devices the DPC verified as DO today)
 *   - Minted today            (QR generated today, customer not done)
 *   - Pending handover        (DO-enabled device + active loan +
 *                              enrollment_verified_at still NULL)
 *
 * Hits GET /api/devices/today. Permission-gated on device.read at
 * the API; the page-level auth gate already controls dashboard access.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Device } from "@dfp/api-client";
import { Card, CardBody, CardHeader, CardTitle } from "./Card.js";
import { api } from "../lib/api.js";

interface TodayResponse {
  readonly enrolledToday: readonly Device[];
  readonly mintedToday: readonly Device[];
  readonly pendingHandover: readonly Device[];
}

export function TodayPanel(): JSX.Element {
  const q = useQuery({
    queryKey: ["devices", "today"],
    queryFn: () => api.get<TodayResponse>("/api/devices/today"),
    refetchInterval: 30_000,
  });

  if (q.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Today on the dealer floor</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-red-700">Failed to load today's enrollment activity.</p>
        </CardBody>
      </Card>
    );
  }

  const data = q.data ?? { enrolledToday: [], mintedToday: [], pendingHandover: [] };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Today on the dealer floor</CardTitle>
        <span className="text-xs text-slate-500">refreshes every 30s</span>
      </CardHeader>
      <CardBody className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Bucket
          label="Enrolled today"
          tone="emerald"
          devices={data.enrolledToday}
          empty="No enrollments completed today."
        />
        <Bucket
          label="Minted today"
          tone="amber"
          devices={data.mintedToday}
          empty="No QRs minted today."
        />
        <Bucket
          label="Pending handover"
          tone="red"
          devices={data.pendingHandover}
          empty="Nothing waiting."
        />
      </CardBody>
    </Card>
  );
}

function Bucket({
  label,
  tone,
  devices,
  empty,
}: {
  label: string;
  tone: "emerald" | "amber" | "red";
  devices: readonly Device[];
  empty: string;
}): JSX.Element {
  const colour =
    tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-red-700";

  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="flex items-baseline justify-between">
        <p className={`text-xs font-bold uppercase tracking-wide ${colour}`}>{label}</p>
        <span className="text-lg font-semibold text-slate-900">{devices.length}</span>
      </div>
      {devices.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">{empty}</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {devices.slice(0, 6).map((d) => (
            <li key={d.id} className="text-xs">
              <Link
                to="/devices/$id"
                params={{ id: d.id }}
                className="text-slate-700 hover:text-slate-900"
              >
                <span className="font-medium">
                  {d.brand} {d.model}
                </span>{" "}
                <span className="text-slate-500">· IMEI {d.imei}</span>
              </Link>
            </li>
          ))}
          {devices.length > 6 ? (
            <li className="text-[11px] text-slate-500">+{devices.length - 6} more</li>
          ) : null}
        </ul>
      )}
    </div>
  );
}
